/**
 * Landing page – explains how AI Tokens works, pricing, and CTA.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Coins,
  Zap,
  Shield,
  Globe,
  CreditCard,
  BarChart3,
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';

// const TOKEN_PACKAGES = [
//   { name: 'Starter', tokens: '200K', price: '$4.99', perToken: '$0.00002495', savings: '' },
//   { name: 'Standard', tokens: '450K', price: '$9.99', perToken: '$0.00002220', savings: '11%' },
//   { name: 'Plus', tokens: '1M', price: '$19.99', perToken: '$0.00001999', savings: '20%', popular: true },
//   { name: 'Pro', tokens: '2.75M', price: '$49.99', perToken: '$0.00001818', savings: '27%' },
//   { name: 'Max', tokens: '6M', price: '$99.99', perToken: '$0.00001665', savings: '33%' },
// ];

const TOKEN_PACKAGES = [
  { name: 'Beta', tokens: '10K', price: '$0', perToken: '$0.0000', savings: '', popular: false  }
];

export default function LandingPage() {
  return (
    <div className="landing">
      {/* ── Beta notice ────────────────────────────────────────── */}
      <div className="beta-banner">
        AI Tokens is in <strong>beta</strong> — some features are under active development.
      </div>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-glow" />
        <div className="container hero-content">
          <div className="hero-badge">
            <Sparkles size={14} />
            <span>Pay-per-use AI tokens</span>
          </div>
          <h1>
            Simple AI access
            <br />
            <span className="gradient-text">No subscription required</span>
          </h1>
          <p className="hero-sub">
            Use tokens to access AI features in supported apps. Pay only for what you use, track your usage, and stay in control of your spend.
          </p>

          <div className="hero-actions">
            <Link to="/register" className="btn btn-primary btn-lg">
              Create Account
              <ArrowRight size={18} />
            </Link>
            <a href="#how-it-works" className="btn btn-ghost btn-lg">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────── */}
      <section id="how-it-works" className="section">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Three simple steps to fund AI features you use</p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <CreditCard size={32} />
              </div>
              <h3>Get Tokens</h3>
              <p>
                Choose a token package that fits your needs. Prices start at just
                $4.99. No subscriptions – top up when you need, and your tokens
                never expire.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <Globe size={32} />
              </div>
              <h3>Use a Supported App</h3>
              <p>
                Open an AI-powered app that supports token-kit. During beta,
                some apps may ask for your personal token string to connect
                usage to your balance.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <BarChart3 size={32} />
              </div>
              <h3>Track & Top Up</h3>
              <p>
                Monitor your usage in real time from your dashboard. See which
                apps and models you use most, and top up whenever you need more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Why AI Tokens?</h2>
            <p>Built for transparency, security, and simplicity</p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <Coins size={24} />
              <h3>Pay Only for What You Use</h3>
              <p>No monthly subscriptions. Tokens are deducted only when an AI request completes successfully.</p>
            </div>
            <div className="feature-card">
              <Shield size={24} />
              <h3>Secure Payments</h3>
              <p>Encrypted token management with SHA-256 hashing. Your tokens are protected by industry-standard security practices.</p>
            </div>
            <div className="feature-card">
              <Globe size={24} />
              <h3>Works in Supported Apps</h3>
              <p>Use your credits in token-kit integrated experiences. Coverage expands as more developers join beta.</p>
            </div>
            <div className="feature-card">
              <Zap size={24} />
              <h3>Instant Credits</h3>
              <p>Tokens appear in your account immediately after purchase. No delays.</p>
            </div>
            <div className="feature-card">
              <BarChart3 size={24} />
              <h3>Full Transparency</h3>
              <p>Your dashboard shows where your tokens go – by app, by model, by request. Stay informed.</p>
            </div>
            <div className="feature-card">
              <CheckCircle2 size={24} />
              <h3>No Expiry</h3>
              <p>Your tokens never expire. Use them today or next year – they're yours to keep. 30-day refund policy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────── */}
      <section id="pricing" className="section">
        <div className="container">
          <div className="section-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Pricing packages to be finalized upon full release of the token-kit service</p>
          </div>

          <div className="pricing-grid">
            {TOKEN_PACKAGES.map((pkg) => (
              <div key={pkg.name} className={`pricing-card ${pkg.popular ? 'popular' : ''}`}>
                {pkg.popular && <div className="popular-badge">Most Popular</div>}
                <h3>{pkg.name}</h3>
                <div className="pricing-tokens">{pkg.tokens} tokens</div>
                <div className="pricing-price">{pkg.price}</div>
                <div className="pricing-per">{pkg.perToken} / token</div>
                {pkg.savings && <div className="pricing-savings">Save {pkg.savings}</div>}
                <Link
                  to="/register"
                  className={`btn ${pkg.popular ? 'btn-primary' : 'btn-outline'} btn-block`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section id="faq" className="section section-alt">
        <div className="container container-sm">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
          </div>

          <div className="faq-list">
            <details className="faq-item">
              <summary>What is an AI Token?</summary>
              <p>
                An AI Token is a unit of credit in the token-kit ecosystem. When
                you use an AI feature in a supported app, tokens are deducted
                based on the AI model used and the length of the request &amp;
                response. Different models have different rates – for example,
                Claude Sonnet 4 costs more per request than GPT-4o Mini.
              </p>
            </details>
            <details className="faq-item">
              <summary>Do tokens expire?</summary>
              <p>
                No! Tokens have lifetime validity. Once purchased, they remain
                in your account until you use them. There are no monthly fees or
                expiration dates.
              </p>
            </details>
            <details className="faq-item">
              <summary>Can I get a refund?</summary>
              <p>
                Yes. Unused tokens are fully refundable within 30 days of
                purchase. Refunds are processed to the original payment method.
              </p>
            </details>
            <details className="faq-item">
              <summary>How do I use my tokens in an app?</summary>
              <p>
                After purchasing tokens, you'll receive a personal user token
                string (e.g., <code>ut_user123_abc456</code>). When you open an
                AI-powered app that uses token-kit, paste this token string
                where prompted. Some apps will support more embedded connection
                flows over time.
              </p>
            </details>
            <details className="faq-item">
              <summary>Do I need my own OpenAI or Anthropic API key?</summary>
              <p>
                No. AI Tokens is designed so end-users can pay through prepaid
                credits without managing provider API keys directly.
              </p>
            </details>
            <details className="faq-item">
              <summary>Which AI models are supported?</summary>
              <p>
                token-kit currently supports Claude 3.5 Haiku, Claude Sonnet 4,
                Amazon Nova Micro, Amazon Nova Lite, GPT-4o, and GPT-4o Mini –
                with more being added regularly.
              </p>
            </details>
            <details className="faq-item">
              <summary>Is my payment information secure?</summary>
              <p>
                Security is a top priority. All tokens are protected with
                SHA-256 hashing, and we follow industry-standard security
                practices. Payment processing details will be shared when
                purchases go live.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <section className="section cta-section">
        <div className="container container-sm" style={{ textAlign: 'center' }}>
          <h2>Ready for Simple AI Billing?</h2>
          <p>
            Join the beta to manage prepaid AI credits with clear usage
            tracking and predictable top-ups.
          </p>
          <Link to="/register" className="btn btn-primary btn-lg">
            Get Started
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
